import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  ErrorBoundary,
  TenantReport,
  type TenantReportData,
  mapRealDataToComponentData,
} from "@multiburo/ui-components";
import { Alert, Button, Spin } from "antd";
import { getApiErrorMessage } from "utils/apiErrorMessage";

import { useGetTenantMultireportPublicData } from "hooks/useTenantReports";

import { decryptTenantReportData } from "js/utils/encryption";
import {
  findCachedDataForToken,
  getReportCacheInfo,
  getReportDataFromSession,
  initializeSessionStorage,
  saveReportDataToSession,
} from "js/utils/sessionStorage";

import "styles/pages/Reports/MultireportView.scss";

import EmailVerificationModal from "./EmailVerificationModal";

const MultireportView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [reportData, setReportData] = useState<TenantReportData | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] =
    useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [usingCachedData, setUsingCachedData] = useState(false);
  const [checkingCache, setCheckingCache] = useState(true);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [fetchNonce, setFetchNonce] = useState(0);
  const [customError, setCustomError] = useState<string | null>(null);

  // Get token from either URL params or query params
  const reportToken = token || searchParams.get("tk");

  // The query fires reactively once email + shouldFetch are set (email is part of
  // the query key). No manual refetch — calling refetch() before the userEmail
  // state has propagated fired a request with an empty email (→ 400).
  const {
    data: apiResponse,
    isLoading: queryLoading,
    error: queryError,
  } = useGetTenantMultireportPublicData(reportToken || "", userEmail, {
    enabled: shouldFetch && !!reportToken && !!userEmail,
    nonce: fetchNonce,
  });

  // Initialize session storage cleanup on component mount
  useEffect(() => {
    initializeSessionStorage();

    // Add classes to html and body to enable scrolling for this page
    document.documentElement.classList.add("multireport-active");
    document.body.classList.add("multireport-page");

    return () => {
      // Remove classes on unmount
      document.documentElement.classList.remove("multireport-active");
      document.body.classList.remove("multireport-page");
    };
  }, []);

  // Process API response when it arrives
  useEffect(() => {
    if (apiResponse && shouldFetch) {
      try {
        let decryptedData;

        // Check if the response is encrypted
        if (typeof apiResponse.data === "string") {
          // Data is encrypted, decrypt it
          const aesKey = import.meta.env.VITE_TENANT_REPORT_AES_KEY;
          decryptedData = decryptTenantReportData(apiResponse.data, aesKey);
        } else {
          // Data is not encrypted (fallback)
          decryptedData = apiResponse.data;
        }

        // Transform the decrypted data to the format expected by TenantReport
        const transformedData = mapRealDataToComponentData(decryptedData);

        // Save the transformed data to session storage for 30 minutes
        if (reportToken && userEmail) {
          saveReportDataToSession(reportToken, userEmail, transformedData);
        }

        setReportData(transformedData);
        setShowEmailModal(false);
        setShouldFetch(false);
      } catch (err: any) {
        console.error("Error processing report data:", err);
        setCustomError("Error al procesar el reporte");
      }
    }
  }, [apiResponse, shouldFetch, reportToken, userEmail]);

  const fetchReportData = async (email: string) => {
    if (!reportToken) {
      setCustomError("Token de acceso no proporcionado");
      return;
    }

    setCustomError(null);
    setUsingCachedData(false);

    // First, check if we have cached data for this token and email
    const cachedData = getReportDataFromSession(reportToken, email);
    if (cachedData) {
      setReportData(cachedData);
      setShowEmailModal(false);
      setUsingCachedData(true);
      return;
    }

    // No cached data, trigger query fetch reactively. Bumping the nonce forces a
    // fresh fetch even when the same email is retried after an error.
    setUserEmail(email);
    setShouldFetch(true);
    setFetchNonce((n) => n + 1);
  };

  const handleEmailVerified = async (email: string) => {
    setEmailVerificationLoading(true);
    setCustomError(null); // Clear any previous errors

    try {
      await fetchReportData(email);
    } catch (error) {
      console.error("Email verification error:", error);
      // Keep the modal open so user can try again with a different email
      setShowEmailModal(true);
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  // Check for cached data and token validity on component mount
  useEffect(() => {
    setCheckingCache(true);

    if (!reportToken) {
      setCustomError("Token de acceso no proporcionado");
      setShowEmailModal(false);
      setCheckingCache(false);
      return;
    }

    // Check if we have cached data for this token
    const cachedInfo = findCachedDataForToken(reportToken);

    if (cachedInfo) {
      // We have cached data! Use it directly without asking for email
      setUserEmail(cachedInfo.email);
      setReportData(cachedInfo.data);
      setUsingCachedData(true);
      setShowEmailModal(false);
    } else {
      // No cached data, show email modal
      setShowEmailModal(true);
    }

    setCheckingCache(false);
  }, [reportToken]);

  const loading = queryLoading || emailVerificationLoading;
  // `description` de un Alert de antd es un HIJO DE REACT: si aqui entrara el
  // objeto del envelope (`{code, message: {email: [...]}}`, que es lo que
  // responde GET /v1/tenants/reports/multireport/public ante un correo
  // invalido) React 19 lanzaria "Objects are not valid as a React child" y la
  // pagina publica del prospecto se caeria completa. `getApiErrorMessage`
  // garantiza texto.
  const error: string | null =
    customError ||
    (queryError
      ? getApiErrorMessage(queryError, "Error al cargar el reporte")
      : null);

  if (loading || checkingCache) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          padding: "20px",
          gap: "16px",
        }}
      >
        <Alert
          message="Error al cargar el reporte"
          description={error}
          type="error"
          showIcon
        />
        {reportToken && (
          <Button
            type="primary"
            onClick={() => {
              setCustomError(null);
              setShowEmailModal(true);
            }}
          >
            Intentar de Nuevo
          </Button>
        )}

        <EmailVerificationModal
          open={showEmailModal}
          onCancel={() => {
            setShowEmailModal(false);
            setCustomError("Acceso cancelado");
          }}
          onEmailVerified={handleEmailVerified}
          loading={emailVerificationLoading}
        />
      </div>
    );
  }

  // Show email modal only if we have a token, no data, no error, and explicitly need email
  if (!reportData && !error && reportToken && showEmailModal) {
    return (
      <div className="multireport-view-container">
        <EmailVerificationModal
          open={showEmailModal}
          onCancel={() => {
            setShowEmailModal(false);
            setCustomError("Acceso cancelado");
          }}
          onEmailVerified={handleEmailVerified}
          loading={emailVerificationLoading}
        />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          padding: "20px",
        }}
      >
        <Alert
          message="Reporte no encontrado"
          description="No se pudo encontrar el reporte solicitado."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  const handleError = (error: Error) => {
    console.error("TenantReport component error:", error);
    setCustomError("Error al renderizar el reporte");
  };

  const getCacheInfo = () => {
    if (reportToken && userEmail) {
      return getReportCacheInfo(reportToken, userEmail);
    }
    return { exists: false };
  };

  const cacheInfo = getCacheInfo();

  const handleWebCheckUnlock = (
    _actorType: "tenant" | "guarantor",
    webCheckId?: string | null,
  ) => {
    if (webCheckId) {
      window.location.href = `/pay/web-check/${webCheckId}`;
    }
  };

  return (
    <div className="multireport-view-container">
      <ErrorBoundary onError={handleError}>
        <TenantReport
          data={reportData}
          onError={handleError}
          className="multireport-view"
          onWebCheckUnlock={handleWebCheckUnlock}
        />
      </ErrorBoundary>
    </div>
  );
};

export default MultireportView;
